import { FormSectionHandler } from '../entity.handler';
import { DeviceResponse, DeviceUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { EntityUtils, ComboField, SectionField, TextField, CheckBoxField } from '../utils';
import { uniq } from 'lodash';
import { FormBuilderField, FormBuilderKeyValue } from 'app/shared/components/form-builder';
import { KeyFormBuilderComboFieldComponent } from 'app/shared/components/form-builder/combo/combo.component';

interface EditorValues {
    active_input: string;
    hours_input: string;
    idling_input: string;
    idling_input_invert: boolean;
}

export interface DeviceParametersResponse extends Pick<DeviceResponse, 'id' | 'owner' | 'entity' | 'parameters'> { }
export interface DeviceParametersUpdateRequest extends Pick<DeviceUpdateRequest, 'parameters'> { }

export const getDeviceTypeIdFromBulk = (devices: any[]): string => {
    const ids = uniq(devices.map(device => device['deviceType'] && device['deviceType'].id));
    return ids.length === 1 ? ids[0] : null;
};

export class DeviceParametersSectionHandler implements FormSectionHandler<DeviceParametersResponse, EditorValues> {

    type = 'form';
    icon = 'wrench';
    bulk = true;
    hidden = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private applyUpdateCallback: (entity: any, payload: any) => Promise<DeviceParametersResponse>, 
    ) {
    }


    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DEVICE.PARAMETERS.${key}`, values);
    }

    applyUpdate(entity: DeviceParametersResponse, payload: DeviceUpdateRequest): Promise<DeviceParametersResponse> {
        return this.applyUpdateCallback(entity, payload);
    }

    mapEntityToEditor(entity: DeviceParametersResponse): Promise<EditorValues> {
        const result: EditorValues = {
            active_input: entity.parameters?.active_input || '',
            hours_input: entity.parameters?.hours_input || '',
            idling_input: entity.parameters?.idling_input || '',
            idling_input_invert: entity.parameters?.idling_input_invert || false,
        };
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: DeviceParametersUpdateRequest, values: EditorValues): Promise<DeviceParametersUpdateRequest> {
        entity.parameters = entity.parameters || {};
        if (values.active_input) {
            entity.parameters.active_input = values.active_input;
        }
        if (values.hours_input) {
            entity.parameters.hours_input = values.hours_input;
        }
        if (values.idling_input) {
            entity.parameters.idling_input = values.idling_input;
        }
        if (values.idling_input_invert !== undefined) {
            entity.parameters.idling_input_invert = values.idling_input_invert;
        }
        return Promise.resolve(entity);
    }

    async getFields(entity: DeviceParametersResponse): Promise<SectionField[]> {

        const deviceTypeId = getDeviceTypeIdFromBulk(entity ? [entity] : this.admin.selectedItems);
        if (deviceTypeId) {
            this.hidden = false;

            const deviceType = await this.admin.getCached(`getDeviceType/${deviceTypeId}/${this.ownerId}`, api => api.entities.getDeviceType(deviceTypeId, this.ownerId));
            const inputs = [
                ...Object.keys(deviceType.ioCapabilities['digital_input']).map(key => ({ key: key, value: key })).sort((a, b) => a.value.localeCompare(b.value)),
                ...Object.keys(deviceType.ioCapabilities['digital_output']).map(key => ({ key: key, value: key })).sort((a, b) => a.value.localeCompare(b.value)),
                { key: '_custom_', value: `${this.admin.translate('EDITORS.DEVICE.IO.CUSTOM')}...` },
            ].map(input => {
                if (input.key === 'ignition') {
                    input.value = this.admin.i18n.instant('ADMIN.EDITORS.DEVICE.IO.PRESET.IGNITION');
                }
                if (input.key === 'movement') {
                    input.value = this.admin.i18n.instant('ADMIN.EDITORS.DEVICE.IO.PRESET.MOVEMENT');
                }
                return input;
            });

            const addCustomValue = (vals: FormBuilderKeyValue[], custom: string): FormBuilderKeyValue[] => {
                if (custom && !vals.find(x => x.key === custom)) {
                    vals.push({ key: custom, value: custom });
                }
                return vals;
            };

            const AddCustomComboField = (id: string, title: string, required: boolean, vals: FormBuilderKeyValue[]): FormBuilderField => {
                const current = entity && entity.parameters && entity.parameters[id];
                return {
                    ...ComboField(id, title, required, addCustomValue(vals, current)),
                    getValue: (field: FormBuilderField, values: any) => values[field.id] || 'none',
                    setValue: async (field: FormBuilderField, values: any, value: any) => {
                        values[field.id] = value;
                        if (value && value === '_custom_') {
                            const result = await this.showCustomModal({ input: '' });
                            if (result && field.component instanceof KeyFormBuilderComboFieldComponent) {
                                field.component.comboValues = addCustomValue(field.component.comboValues, result.input);
                            }
                            values[field.id] = result?.input;
                        } 
                    },
                };
            };

            const fields = [
                AddCustomComboField('active_input', this.translate('FIELDS.ACTIVE_INPUT'), true, [{ key: 'none', value: this.admin.i18n.instant('SHARED.NONE') }, { key: 'emulate', value: this.admin.i18n.instant('ADMIN.EDITORS.DEVICE.IO.PRESET.EMULATE') }, ...inputs]),
                AddCustomComboField('hours_input', this.translate('FIELDS.HOURS_INPUT'), true, [{ key: 'default', value: this.admin.i18n.instant('ADMIN.EDITORS.DEVICE.IO.PRESET.DEFAULT') }, ...inputs]),
                AddCustomComboField('idling_input', this.translate('FIELDS.IDLING_INPUT'), true, [{ key: 'speed', value: this.admin.i18n.instant('ADMIN.EDITORS.DEVICE.IO.PRESET.SPEED') }, ...inputs]),
                CheckBoxField('idling_input_invert', this.translate('FIELDS.IDLING_INPUT_INVERT'), true),
            ];

            return EntityUtils.prepareBulk(!entity, ['active_input', 'hours_input', 'idling_input', 'idling_input_invert'], fields);
        } else {
            this.hidden = true;
            return Promise.resolve([]);
        }
    }

    showCustomModal(input: any): Promise<any> {
        const translate = (key: string, values?: Object): string => {
            return this.admin.translate(`EDITORS.DEVICE.IO.CUSTOM_MODAL.${key}`, values);
        };
        return this.admin.showFormModal<any>({
            groups: [{
                name: translate('TITLE'),
                description: translate('DESC'),
                fields: [
                    TextField('input', translate('FIELDS.INPUT'), true, 1, 255),
                ],
            }],
        }, input);

    }

}


